import React, { useState, useEffect, useCallback } from 'react';
import './SourcesManager.css';
import { API_CONFIG } from '../../config/api';

interface NewsSource {
  name: string;
  url: string;
  type: 'rss' | 'web';
  enabled: boolean;
  category?: string;
  lastScrapedAt?: string;
  lastError?: string;
  articlesFound?: number;
  status?: 'working' | 'error' | 'unknown';
  // Health monitoring fields
  healthStatus?: 'healthy' | 'failed' | 'empty' | 'unknown';
  healthStatusIcon?: string;
  healthError?: string;
  lastHealthCheck?: string;
  lastHealthCheckFormatted?: string;
  articleCount?: number;
  articleCountDisplay?: number;
  enabledIcon?: string;
}

interface SourcesConfig {
  sources: NewsSource[];
  scraping: {
    maxArticlesPerSource: number;
    relevanceThreshold: number;
    deduplicationEnabled: boolean;
    aiKeywords: string;
    businessKeywords: string;
  };
}

const SourcesManager: React.FC = () => {
  const [config, setConfig] = useState<SourcesConfig>({
    sources: [],
    scraping: {
      maxArticlesPerSource: 10,
      relevanceThreshold: 0.1,
      deduplicationEnabled: true,
      aiKeywords: "",
      businessKeywords: ""
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);

  const updateSourcesWithStatus = useCallback((scrapeResult: any) => {
    setConfig(prevConfig => ({
      ...prevConfig,
      sources: prevConfig.sources.map(source => {
        const sourceResult = scrapeResult.sources?.find((s: any) => s.name === source.name);
        const sourceError = scrapeResult.errors?.find((error: string) => 
          error.includes(source.name)
        );

        return {
          ...source,
          lastScrapedAt: scrapeResult.timestamp,
          articlesFound: sourceResult?.articlesFound || 0,
          lastError: sourceError || undefined,
          status: sourceError ? 'error' : (sourceResult?.articlesFound > 0 ? 'working' : 'unknown')
        };
      })
    }));
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/sources`);
      if (response.ok) {
        const payload = await response.json();
        const data = payload?.data || payload;
        setConfig(data);
      } else {
        // If no config exists, use default
        setConfig({
          sources: [
            { name: 'TechCrunch AI', url: 'https://techcrunch.com/category/artificial-intelligence/feed/', type: 'rss', enabled: true, category: 'AI' },
            { name: 'VentureBeat AI', url: 'https://venturebeat.com/ai/feed/', type: 'rss', enabled: true, category: 'AI' },
            { name: 'MIT Technology Review AI', url: 'https://www.technologyreview.com/topic/artificial-intelligence/feed/', type: 'rss', enabled: true, category: 'AI' },
            { name: 'AI News', url: 'https://www.artificialintelligence-news.com/feed/', type: 'rss', enabled: true, category: 'AI' },
            { name: 'The Verge AI', url: 'https://www.theverge.com/ai-artificial-intelligence/rss/index.xml', type: 'rss', enabled: true, category: 'AI' },
            { name: 'Ars Technica AI', url: 'https://feeds.arstechnica.com/arstechnica/technology-lab', type: 'rss', enabled: true, category: 'Tech' },
            { name: 'Wired AI', url: 'https://www.wired.com/feed/tag/ai/latest/rss', type: 'rss', enabled: true, category: 'AI' },
            { name: 'IEEE Spectrum AI', url: 'https://spectrum.ieee.org/rss/blog/artificial-intelligence', type: 'rss', enabled: true, category: 'AI' }
          ],
          scraping: {
            maxArticlesPerSource: 10,
            relevanceThreshold: 0.1,
            deduplicationEnabled: true,
            aiKeywords: "ai,artificial intelligence,machine learning,ml,deep learning,neural network,automation,robot,algorithm,data science,nlp,computer vision,chatbot,llm,gpt,openai,anthropic",
            businessKeywords: "startup,funding,investment,business,enterprise,company,market,industry,revenue,growth,technology,innovation,venture capital,ipo,acquisition,merger"
          }
        });
      }
    } catch (error) {
      console.error('Error loading config:', error);
      setMessage({type: 'error', text: 'Failed to load configuration'});
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const saveConfig = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/sources`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config),
      });

      if (response.ok) {
        setMessage({type: 'success', text: 'Configuration saved successfully!'});
      } else {
        throw new Error('Failed to save configuration');
      }
    } catch (error) {
      console.error('Error saving config:', error);
      setMessage({type: 'error', text: 'Failed to save configuration'});
    } finally {
      setIsSaving(false);
    }
  };

  const addSource = () => {
    setConfig(prev => ({
      ...prev,
      sources: [...prev.sources, {
        name: '',
        url: '',
        type: 'rss',
        enabled: true,
        category: 'AI'
      }]
    }));
  };

  const updateSource = (index: number, field: keyof NewsSource, value: any) => {
    setConfig(prev => ({
      ...prev,
      sources: prev.sources.map((source, i) => 
        i === index ? { ...source, [field]: value } : source
      )
    }));
  };

  const removeSource = (index: number) => {
    setConfig(prev => ({
      ...prev,
      sources: prev.sources.filter((_, i) => i !== index)
    }));
  };

  const updateScrapingConfig = (field: string, value: any) => {
    const defaultScraping = {
      maxArticlesPerSource: 50,
      relevanceThreshold: 0.1,
      deduplicationEnabled: true,
      aiKeywords: "",
      businessKeywords: ""
    };
    
    setConfig(prev => ({
      ...prev,
      scraping: { 
        ...defaultScraping,
        ...prev.scraping, 
        [field]: value 
      }
    }));
  };

  if (isLoading) {
    return <div className="loading">Loading configuration...</div>;
  }

  return (
    <div className="sources-manager">
      <div className="section">
        <h2>Scraping Configuration</h2>
        <div className="config-grid">
          <div className="config-item">
            <label>Max Articles Per Source:</label>
            <input
              type="number"
              value={config.scraping?.maxArticlesPerSource || 50}
              onChange={(e) => updateScrapingConfig('maxArticlesPerSource', parseInt(e.target.value))}
              min="1"
            />
          </div>
          <div className="config-item">
            <label>Relevance Threshold:</label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="1"
              value={config.scraping?.relevanceThreshold || 0.1}
              onChange={(e) => updateScrapingConfig('relevanceThreshold', parseFloat(e.target.value))}
            />
          </div>
          <div className="config-item">
            <label>
              <input
                type="checkbox"
                checked={config.scraping?.deduplicationEnabled || true}
                onChange={(e) => updateScrapingConfig('deduplicationEnabled', e.target.checked)}
              />
              Enable Deduplication
            </label>
          </div>
          <div className="config-item keywords-field">
            <label>AI Keywords (comma-separated):</label>
            <textarea
              value={config.scraping?.aiKeywords || ""}
              onChange={(e) => updateScrapingConfig('aiKeywords', e.target.value)}
              placeholder="ai,artificial intelligence,machine learning..."
              rows={3}
            />
          </div>
          <div className="config-item keywords-field">
            <label>Business Keywords (comma-separated):</label>
            <textarea
              value={config.scraping?.businessKeywords || ""}
              onChange={(e) => updateScrapingConfig('businessKeywords', e.target.value)}
              placeholder="startup,funding,investment,business..."
              rows={3}
            />
          </div>
        </div>
      </div>

      <div className="section">
        <div className="section-header">
          <h2>News Sources ({config.sources.length})</h2>
          <button onClick={addSource} className="add-button">Add Source</button>
        </div>

        <div className="sources-list">
          {config.sources.map((source, index) => (
            <div key={index} className={`source-item ${source.status}`}>
              <div className="source-controls">
                <div className="source-status">
                  {/* Health Status Display */}
                  {source.healthStatusIcon && (
                    <span className={`health-badge ${source.healthStatus}`}>
                      {source.healthStatusIcon} {source.healthStatus}
                    </span>
                  )}
                  
                  {/* Enabled/Disabled Status */}
                  {source.enabledIcon && (
                    <span className={`enabled-badge ${source.enabled ? 'enabled' : 'disabled'}`}>
                      {source.enabledIcon} {source.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  )}
                  
                  {/* Article Count */}
                  {source.articleCountDisplay !== undefined && (
                    <span className="articles-count">{source.articleCountDisplay} articles</span>
                  )}
                  
                  {/* Last Health Check */}
                  {source.lastHealthCheckFormatted && (
                    <span className="last-check">Last: {source.lastHealthCheckFormatted}</span>
                  )}
                  
                  {/* Health Error */}
                  {source.healthError && (
                    <span className="health-error" title={source.healthError}>
                      ⚠️ {source.healthError.substring(0, 50)}...
                    </span>
                  )}
                  
                  {/* Fallback to old status system */}
                  {!source.healthStatusIcon && source.status === 'working' && <span className="status-badge working">✅ Working</span>}
                  {!source.healthStatusIcon && source.status === 'error' && <span className="status-badge error">❌ Error</span>}
                  {!source.healthStatusIcon && source.status === 'unknown' && <span className="status-badge unknown">❓ Unknown</span>}
                  {!source.healthStatusIcon && source.articlesFound !== undefined && (
                    <span className="articles-count">{source.articlesFound} articles</span>
                  )}
                </div>
                <button 
                  onClick={() => removeSource(index)}
                  className="remove-button"
                  title="Remove source"
                >
                  ×
                </button>
              </div>
              
              <div className="source-fields">
                <input
                  type="text"
                  placeholder="Source Name"
                  value={source.name}
                  onChange={(e) => updateSource(index, 'name', e.target.value)}
                />
                <input
                  type="url"
                  placeholder="RSS Feed URL"
                  value={source.url}
                  onChange={(e) => updateSource(index, 'url', e.target.value)}
                />
                <select
                  value={source.type}
                  onChange={(e) => updateSource(index, 'type', e.target.value as 'rss' | 'web')}
                >
                  <option value="rss">RSS Feed</option>
                  <option value="web">Web Scraping</option>
                </select>
                <input
                  type="text"
                  placeholder="Category"
                  value={source.category || ''}
                  onChange={(e) => updateSource(index, 'category', e.target.value)}
                />
              </div>
              
              {source.lastError && (
                <div className="source-error">
                  <strong>Last Error:</strong> {source.lastError}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {message && (
        <div className={`status-message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="actions">
        <button 
          onClick={saveConfig} 
          disabled={isSaving}
          className="save-button"
        >
          {isSaving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>
    </div>
  );
};

export default SourcesManager;
